import React, { useEffect, useRef, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { useMount } from "react-use";
import { toast } from "sonner";

import DisplaysForm from "./DisplaysForm";
import { notifError } from "../../actions/FlashMessageActions";
import SmartlistAPI from "../../api/SmartlistAPI";
import "../../css/admin/smartlist.scss";
import useAbility from "../../hooks/useAbility";
import { getDefaultProjectLanguage } from "../../utils/data/Content";
import { PROJECTS_ROUTE } from "../routes/routes";

import type { JSX } from "react";

import type { GetDisplayResponse } from "../../api/SmartlistAPI.types";
import type { AppState } from "../../reducers";

export default function DisplaysAdminUpdate(): JSX.Element {
  const { t } = useTranslation(["editor-displays"]);
  const { id, projectSlug } = useParams<{
    id: string;
    projectSlug: string;
  }>();
  const project = useSelector(
    (state: AppState) => state.architectureReducer.project,
  );
  const languages = useSelector(
    (state: AppState) => state.fetchReducer.languages,
  );

  const { language_uuid: languageUuid } = getDefaultProjectLanguage(
    languages,
    project,
  );

  const isNewDisplay = id === "new";

  const newDisplayData = {
    begin_date: moment().format(),
    enabled: true,
    countries: [],
    end_date: null,
    geoblock_mode: "disabled",
    label: t("newDisplay"),
    target_blank: true,
    temporal_restriction: false,
    translations: [
      {
        default: true,
        language_uuid: languageUuid,
        title: "",
        url: "",
      },
    ],
  };

  const dispatch = useDispatch();

  const [display, setDisplay] = useState<GetDisplayResponse>();

  const queryClient = useQueryClient();
  const history = useHistory();

  const orgaOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );

  const { canAccess } = useAbility();

  const { project: canAccessProject } = canAccess(orgaOptions);

  const getDisplayQuery = SmartlistAPI.generateQuery({
    fn: SmartlistAPI.getDisplay,
    params: [{ uuid: id, projectSlug }],
    options: {
      enabled: Boolean(id) && !isNewDisplay,
    },
  });

  const { data: displayData } = useQuery(getDisplayQuery);

  const getDisplayListQuery = SmartlistAPI.generateQuery({
    fn: SmartlistAPI.getDisplayList,
    params: [
      {
        projectSlug,
      },
    ],
  });

  const createDisplayMutation = useMutation({
    mutationFn: async () =>
      SmartlistAPI.createDisplay({
        data: newDisplayData,
        projectSlug,
      }),
    onSuccess: ({ uuid }) => {
      queryClient.invalidateQueries(getDisplayListQuery.queryKey);

      history.replace(`${PROJECTS_ROUTE}/${projectSlug}/displays/${uuid}`);
    },
    onError: () => {
      toast.error(t("createError", { ns: "editor-notif" }));
    },
  });

  const hasCreatedRef = useRef(false);

  useMount(() => {
    if (isNewDisplay && !hasCreatedRef.current) {
      hasCreatedRef.current = true;
      if (canAccessProject.displaysWrite) {
        createDisplayMutation.mutate();
      } else {
        history.replace(`${PROJECTS_ROUTE}/${projectSlug}/displays/`);
      }
    }
  });

  useEffect(() => {
    setDisplay(displayData);
  }, [displayData]);

  const itemIsValid = (item: GetDisplayResponse): string[] => {
    const errors: string[] = [];

    if (!item.label || item.label.length === 0) {
      errors.push(t("editor-errors:noLabel"));
    }

    if (
      item.end_date &&
      moment(item.end_date).isBefore(moment(item.begin_date))
    ) {
      errors.push(t("editor-errors:endBeforeStartDate"));
    }

    return errors;
  };

  const updateDisplayMutation = useMutation({
    mutationFn: async ({
      uuid,
      data,
    }: {
      uuid: string;
      data: GetDisplayResponse;
    }): Promise<GetDisplayResponse> =>
      SmartlistAPI.updateDisplay({
        uuid,
        data,
        projectSlug,
      }),

    onSuccess: () => {
      toast.success(t("editor-notif:saveSuccess"));

      // Todo update PUT backend response for optimistic updates
      queryClient.invalidateQueries({ queryKey: getDisplayQuery.queryKey });

      queryClient.invalidateQueries(getDisplayListQuery.queryKey);
    },

    onError: () => {
      toast.error(t("editor-notif:saveError"));
    },
  });

  const handleUpdate = (
    updatedDisplay: GetDisplayResponse,
    onSuccess?: () => void,
  ): void => {
    const errors = itemIsValid(updatedDisplay);

    if (errors.length === 0) {
      updateDisplayMutation.mutate(
        { uuid: updatedDisplay.uuid, data: updatedDisplay },
        { onSuccess },
      );
    } else {
      dispatch(notifError(t("editor-notif:saveError"), errors));
    }
  };

  return (
    <div className="editor-smartlist-update-container">
      {display ? (
        <DisplaysForm
          display={display}
          onSubmit={handleUpdate}
          setDisplay={
            setDisplay as React.Dispatch<
              React.SetStateAction<GetDisplayResponse>
            >
          }
        />
      ) : null}
    </div>
  );
}
